import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GET, POST, PUT, DELETE, FORM_POST } from '../services/api';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';
import {
  Briefcase,
  LayoutTemplate,
  Plus,
  Pencil,
  Trash2,
  X,
  CheckCircle,
  AlertTriangle,
  Info,
  XCircle,
  Loader2,
  Image as ImageIcon,
} from 'lucide-react';

const CustomStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 8px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94a3b8; }
  `}</style>
);

const CustomToast = ({ message, severity, onClose }) => {
  if (!message) return null;
  const styles = {
    success: { bg: 'bg-emerald-600', icon: <CheckCircle className="w-6 h-6" /> },
    info: { bg: 'bg-blue-600', icon: <Info className="w-6 h-6" /> },
    warn: { bg: 'bg-amber-500', icon: <AlertTriangle className="w-6 h-6" /> },
    error: { bg: 'bg-red-500', icon: <XCircle className="w-6 h-6" /> },
  };
  const style = styles[severity] || styles.info;

  return (
    <div className={`fixed bottom-6 right-6 z-[100] px-6 py-3 rounded-xl shadow-2xl text-white flex items-center gap-3 ${style.bg}`}>
      {style.icon}
      <div className="flex-1">
        <div className="font-bold text-sm uppercase tracking-wide">{severity}</div>
        <div className="text-sm font-medium">{message}</div>
      </div>
      <button onClick={onClose} className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const ConfirmDialog = ({ isOpen, header, message, onAccept, onReject }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-[110] p-4">
      <div className="bg-white p-6 rounded-3xl shadow-2xl border border-slate-100 w-full max-w-sm text-center">
        <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-red-50">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-2">{header}</h3>
        <p className="text-slate-500 mb-6 font-medium">{message}</p>
        <div className="flex gap-3">
          <button onClick={onReject} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors">
            Cancel
          </button>
          <button onClick={onAccept} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-colors">
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const Sponsor = () => {
  const [activeTab, setActiveTab] = useState('bar');

  const [barSponsors, setBarSponsors] = useState([]);
  const [bannerSponsors, setBannerSponsors] = useState([]);
  const [cities, setCities] = useState([]);
  const [bannerChapters, setBannerChapters] = useState([]);

  const [loadingBar, setLoadingBar] = useState(false);
  const [loadingBanner, setLoadingBanner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showBarModal, setShowBarModal] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  // Super-admin master chapter filter: narrows the banner list and pre-fills new banners (bar sponsors stay global)
  const master = useMasterChapter();

  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState('info');

  const [confirmDialogState, setConfirmDialogState] = useState({
    isOpen: false,
    header: '',
    message: '',
    onAccept: () => {},
    onReject: () => {},
  });

  const [barForm, setBarForm] = useState({
    id: null,
    tagline: '',
    startDate: '',
    endDate: '',
    isActive: true,
  });

  const [bannerForm, setBannerForm] = useState({
    id: null,
    city_id: '',
    chapter_id: '',
    startDate: '',
    endDate: '',
    isActive: true,
    bannerImage: null,
    existingImages: [],
  });

  const showToast = useCallback((message, severity = 'info') => {
    setToastMessage(message);
    setToastSeverity(severity);
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  const resetBarForm = useCallback(() => {
    setBarForm({
      id: null,
      tagline: '',
      startDate: '',
      endDate: '',
      isActive: true,
    });
  }, []);

  const resetBannerForm = useCallback(() => {
    setBannerForm({
      id: null,
      city_id: master.cityId || '',
      chapter_id: master.chapterId || '',
      startDate: '',
      endDate: '',
      isActive: true,
      bannerImage: null,
      existingImages: [],
    });
    // With a master city the chapter list is (re)loaded by the city_id effect, so keep it
    if (!master.cityId) setBannerChapters([]);
  }, [master.cityId, master.chapterId]);

  const fetchBarSponsors = useCallback(async () => {
    setLoadingBar(true);
    try {
      const result = await GET('/get-all-bar-sponsors');
      setBarSponsors(result?.data || []);
    } catch (error) {
      showToast(error.message || 'Failed to fetch bar sponsors', 'error');
    } finally {
      setLoadingBar(false);
    }
  }, [showToast]);

  const fetchBannerSponsors = useCallback(async () => {
    setLoadingBanner(true);
    try {
      const query = master.chapterId ? `?chapter_id=${master.chapterId}&city_id=${master.cityId}` : '';
      const result = await GET(`/get-all-banner-sponser${query}`);
      setBannerSponsors(result?.data || []);
    } catch (error) {
      showToast(error.message || 'Failed to fetch banner sponsors', 'error');
    } finally {
      setLoadingBanner(false);
    }
  }, [showToast, master.chapterId, master.cityId]);

  useEffect(() => {
    fetchBarSponsors();
  }, [fetchBarSponsors]);

  useEffect(() => {
    fetchBannerSponsors();
  }, [fetchBannerSponsors]);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const data = await GET('/settings/cities');
        setCities(Array.isArray(data) ? data : data?.data || []);
      } catch (error) {
        console.error('Failed to fetch cities', error);
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    const loadChapters = async () => {
      if (!bannerForm.city_id) {
        setBannerChapters([]);
        return;
      }
      try {
        const result = await GET(`/dashboard/chapters/city/${bannerForm.city_id}`);
        setBannerChapters(result?.data || result || []);
      } catch (error) {
        console.error('Failed to fetch chapters', error);
        setBannerChapters([]);
      }
    };
    loadChapters();
  }, [bannerForm.city_id]);

  const isBarEditing = useMemo(() => Boolean(barForm.id), [barForm.id]);
  const isBannerEditing = useMemo(() => Boolean(bannerForm.id), [bannerForm.id]);

  const handleBarSubmit = async (event) => {
    event.preventDefault();

    if (!barForm.tagline || !barForm.startDate || !barForm.endDate) {
      showToast('Please fill all required fields', 'warn');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        tagline: barForm.tagline,
        startDate: barForm.startDate,
        endDate: barForm.endDate,
        isActive: barForm.isActive,
      };

      if (isBarEditing) {
        await PUT(`/update-bar-sponsor/${barForm.id}`, payload);
        showToast('Bar sponcer updated successfully', 'success');
      } else {
        await POST('/create-bar-sponsor', payload);
        showToast('Bar sponcer created successfully', 'success');
      }

      setShowBarModal(false);
      resetBarForm();
      fetchBarSponsors();
    } catch (error) {
      showToast(error.message || 'Failed to save bar sponcer', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBannerSubmit = async (event) => {
    event.preventDefault();

    if (!bannerForm.startDate || !bannerForm.endDate) {
      showToast('Please fill all required fields', 'warn');
      return;
    }

    if (!isBannerEditing && !bannerForm.bannerImage) {
      showToast('Banner image is required for new entry', 'warn');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('startDate', bannerForm.startDate);
      formData.append('endDate', bannerForm.endDate);
      formData.append('isActive', String(bannerForm.isActive));
      formData.append('city_id', bannerForm.city_id || '');
      formData.append('chapter_id', bannerForm.chapter_id || '');

      if (bannerForm.bannerImage) {
        formData.append('banner_image', bannerForm.bannerImage);
      }

      if (isBannerEditing) {
        await FORM_POST(`/update-banner-sponsor/${bannerForm.id}`, formData, 'PUT');
        showToast('Banner sponcer updated successfully', 'success');
      } else {
        await FORM_POST('/create-banner-sponsor', formData, 'POST');
        showToast('Banner sponcer created successfully', 'success');
      }

      setShowBannerModal(false);
      resetBannerForm();
      fetchBannerSponsors();
    } catch (error) {
      showToast(error.message || 'Failed to save banner sponcer', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBarEdit = (item) => {
    setBarForm({
      id: item._id,
      tagline: item.tagline || '',
      startDate: formatDateForInput(item.startDate),
      endDate: formatDateForInput(item.endDate),
      isActive: item.isActive !== false,
    });
    setActiveTab('bar');
    setShowBarModal(true);
  };

  const handleBannerEdit = (item) => {
    setBannerForm({
      id: item._id,
      city_id: item.city_id?._id || item.city_id || '',
      chapter_id: item.chapter_id?._id || item.chapter_id || '',
      startDate: formatDateForInput(item.startDate),
      endDate: formatDateForInput(item.endDate),
      isActive: item.isActive !== false,
      bannerImage: null,
      existingImages: Array.isArray(item.banner_image) ? item.banner_image : [],
    });
    setActiveTab('banner');
    setShowBannerModal(true);
  };

  const showDeleteConfirm = (header, message, callback) => {
    setConfirmDialogState({
      isOpen: true,
      header,
      message,
      onAccept: async () => {
        await callback();
        setConfirmDialogState((prev) => ({ ...prev, isOpen: false }));
      },
      onReject: () => setConfirmDialogState((prev) => ({ ...prev, isOpen: false })),
    });
  };

  const handleBarDelete = (id) => {
    showDeleteConfirm('Delete Bar Sponcer', 'Are you sure you want to delete this bar sponcer?', async () => {
      try {
        await DELETE(`/delete-bar-sponsor/${id}`);
        showToast('Bar sponcer deleted successfully', 'success');
        fetchBarSponsors();
        if (barForm.id === id) resetBarForm();
      } catch (error) {
        showToast(error.message || 'Failed to delete bar sponcer', 'error');
      }
    });
  };

  const handleBannerDelete = (id) => {
    showDeleteConfirm('Delete Banner Sponcer', 'Are you sure you want to delete this banner sponcer?', async () => {
      try {
        await DELETE(`/delete-banner-sponsor/${id}`);
        showToast('Banner sponcer deleted successfully', 'success');
        fetchBannerSponsors();
        if (bannerForm.id === id) resetBannerForm();
      } catch (error) {
        showToast(error.message || 'Failed to delete banner sponcer', 'error');
      }
    });
  };

  const navItems = [
    { id: 'bar', label: 'Bar Sponcer', icon: Briefcase },
    { id: 'banner', label: 'Banner Sponcer', icon: LayoutTemplate },
  ];

  return (
    <div>
      <CustomStyles />
      <CustomToast message={toastMessage} severity={toastSeverity} onClose={() => setToastMessage(null)} />
      <ConfirmDialog
        isOpen={confirmDialogState.isOpen}
        header={confirmDialogState.header}
        message={confirmDialogState.message}
        onAccept={confirmDialogState.onAccept}
        onReject={confirmDialogState.onReject}
      />

      {showBarModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-[105] p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-slate-800">{isBarEditing ? 'Update Bar Sponcer' : 'Create Bar Sponcer'}</h2>
              <button
                onClick={() => {
                  setShowBarModal(false);
                  resetBarForm();
                }}
                className="p-2 rounded-full text-slate-500 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleBarSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Tagline</label>
                <input
                  type="text"
                  value={barForm.tagline}
                  onChange={(event) => setBarForm((prev) => ({ ...prev, tagline: event.target.value }))}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter tagline"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Start Date</label>
                <input
                  type="date"
                  value={barForm.startDate}
                  onChange={(event) => setBarForm((prev) => ({ ...prev, startDate: event.target.value }))}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">End Date</label>
                <input
                  type="date"
                  value={barForm.endDate}
                  onChange={(event) => setBarForm((prev) => ({ ...prev, endDate: event.target.value }))}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="md:col-span-2 flex items-center justify-between pt-2">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-600">
                  <input
                    type="checkbox"
                    checked={barForm.isActive}
                    onChange={(event) => setBarForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                    className="w-4 h-4"
                  />
                  Is Active
                </label>

                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {isBarEditing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBannerModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex justify-center items-center z-[105] p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-slate-800">{isBannerEditing ? 'Update Banner Sponcer' : 'Create Banner Sponcer'}</h2>
              <button
                onClick={() => {
                  setShowBannerModal(false);
                  resetBannerForm();
                }}
                className="p-2 rounded-full text-slate-500 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleBannerSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Start Date</label>
                <input
                  type="date"
                  value={bannerForm.startDate}
                  onChange={(event) => setBannerForm((prev) => ({ ...prev, startDate: event.target.value }))}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">End Date</label>
                <input
                  type="date"
                  value={bannerForm.endDate}
                  onChange={(event) => setBannerForm((prev) => ({ ...prev, endDate: event.target.value }))}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">City (Optional)</label>
                <select
                  value={bannerForm.city_id}
                  onChange={(event) =>
                    setBannerForm((prev) => ({
                      ...prev,
                      city_id: event.target.value,
                      chapter_id: '',
                    }))
                  }
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Select city (optional)</option>
                  {cities.map((city) => (
                    <option key={city._id} value={city._id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Chapter (Optional)</label>
                <select
                  value={bannerForm.chapter_id}
                  onChange={(event) =>
                    setBannerForm((prev) => ({ ...prev, chapter_id: event.target.value }))
                  }
                  disabled={!bannerForm.city_id}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:bg-slate-50 disabled:text-slate-400"
                >
                  <option value="">{bannerForm.city_id ? 'Select chapter (optional)' : 'Select city first'}</option>
                  {bannerChapters.map((chapter) => (
                    <option key={chapter._id} value={chapter._id}>
                      {chapter.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Banner Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => setBannerForm((prev) => ({ ...prev, bannerImage: event.target.files?.[0] || null }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm file:mr-2 file:px-3 file:py-1 file:border-0 file:rounded-lg file:bg-slate-100 file:text-slate-700"
                />
              </div>

              {isBannerEditing && bannerForm.existingImages.length > 0 && (
                <div className="md:col-span-2">
                  <p className="text-xs font-bold text-slate-500 uppercase mb-2">Current Image</p>
                  <div className="flex flex-wrap gap-3">
                    {bannerForm.existingImages.map((imageUrl) => (
                      <img
                        key={imageUrl}
                        src={imageUrl}
                        alt="Banner"
                        className="w-24 h-16 object-cover rounded-lg border border-slate-200"
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="md:col-span-2 flex items-center justify-between pt-2">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-600">
                  <input
                    type="checkbox"
                    checked={bannerForm.isActive}
                    onChange={(event) => setBannerForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                    className="w-4 h-4"
                  />
                  Is Active
                </label>

                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {isBannerEditing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="space-y-5">
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-2 overflow-x-auto custom-scrollbar">
          <nav className="flex space-x-1 min-w-max">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`px-4 py-3 rounded-2xl text-sm font-bold flex items-center space-x-2 transition-all ${
                  activeTab === item.id
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'bar' && (
          <div className="space-y-5">
            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-800">Bar Sponcer List</h3>
                <button
                  onClick={() => {
                    resetBarForm();
                    setShowBarModal(true);
                  }}
                  className="px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Create
                </button>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50">
                    <tr>
                      {['Tagline', 'Start Date', 'End Date', 'Status', 'Actions'].map((header) => (
                        <th key={header} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {loadingBar ? (
                      <tr>
                        <td colSpan="5" className="text-center py-10 text-slate-400">Loading...</td>
                      </tr>
                    ) : barSponsors.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-10 text-slate-400">No bar sponcer found.</td>
                      </tr>
                    ) : (
                      barSponsors.map((item) => (
                        <tr key={item._id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-5 py-3 text-sm font-semibold text-slate-800">{item.tagline}</td>
                          <td className="px-5 py-3 text-sm text-slate-600">{formatDateForDisplay(item.startDate)}</td>
                          <td className="px-5 py-3 text-sm text-slate-600">{formatDateForDisplay(item.endDate)}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full border ${item.isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                              {item.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap text-sm flex gap-2">
                            <button onClick={() => handleBarEdit(item)} className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleBarDelete(item._id)} className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'banner' && (
          <div className="space-y-5">
            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-800">Banner Sponcer List</h3>
                <button
                  onClick={() => {
                    resetBannerForm();
                    setShowBannerModal(true);
                  }}
                  className="px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Create
                </button>
              </div>
              {master.chapterId && <div className="px-6 pt-3"><MasterChapterLockNote /></div>}

              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full divide-y divide-slate-100">
                  <thead className="bg-slate-50">
                    <tr>
                      {['Image', 'City', 'Chapter', 'Start Date', 'End Date', 'Status', 'Actions'].map((header) => (
                        <th key={header} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {loadingBanner ? (
                      <tr>
                        <td colSpan="7" className="text-center py-10 text-slate-400">Loading...</td>
                      </tr>
                    ) : bannerSponsors.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-10 text-slate-400">No banner sponcer found.</td>
                      </tr>
                    ) : (
                      bannerSponsors.map((item) => {
                        const firstImage = Array.isArray(item.banner_image) && item.banner_image.length > 0 ? item.banner_image[0] : null;
                        return (
                          <tr key={item._id} className="hover:bg-slate-50/80 transition-colors">
                            <td className="px-6 py-4">
                              {firstImage ? (
                                <img src={firstImage} alt="Banner" className="w-20 h-14 object-cover rounded-lg border border-slate-200" />
                              ) : (
                                <div className="w-20 h-14 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                                  <ImageIcon className="w-4 h-4" />
                                </div>
                              )}
                            </td>
                            <td className="px-5 py-3 text-sm text-slate-600 whitespace-nowrap">
                              {item.city_id?.name || '-'}
                            </td>
                            <td className="px-5 py-3 text-sm text-slate-600 whitespace-nowrap">
                              {item.chapter_id?.name || '-'}
                            </td>
                            <td className="px-5 py-3 text-sm text-slate-600">{formatDateForDisplay(item.startDate)}</td>
                            <td className="px-5 py-3 text-sm text-slate-600">{formatDateForDisplay(item.endDate)}</td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full border ${item.isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                                {item.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-5 py-3 whitespace-nowrap text-sm flex gap-2">
                              <button onClick={() => handleBannerEdit(item)} className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleBannerDelete(item._id)} className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sponsor;